import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import { Star as StarIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import reviewsService, { Review } from '../../api/services/reviews';

const Reviews: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [response, setResponse] = useState('');
  const [isResponseDialogOpen, setIsResponseDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await reviewsService.getReviews(1);
      setReviews(data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setError('Failed to load reviews. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenResponseDialog = (review: Review) => {
    setSelectedReview(review);
    setResponse(review.response || '');
    setIsResponseDialogOpen(true);
  };

  const handleCloseResponseDialog = () => {
    setSelectedReview(null);
    setResponse('');
    setIsResponseDialogOpen(false);
    setError(null);
  };

  const handleGenerateResponse = async () => {
    if (!selectedReview) return;

    try {
      setIsGeneratingResponse(true);
      setError(null);
      const { response: generatedResponse } = await reviewsService.generateResponse(
        selectedReview.id,
        { tone: 'professional' }
      );
      setResponse(generatedResponse);
    } catch (error) {
      console.error('Error generating response:', error);
      setError('Failed to generate AI response. Please try again.');
    } finally {
      setIsGeneratingResponse(false);
    }
  };

  const handleSubmitResponse = async () => {
    if (!selectedReview || !response) return;

    try {
      setIsSubmittingResponse(true);
      setError(null);
      await reviewsService.respondToReview(selectedReview.id, response);
      await fetchReviews();
      handleCloseResponseDialog();
    } catch (error) {
      console.error('Error submitting response:', error);
      setError('Failed to submit response. Please try again.');
    } finally {
      setIsSubmittingResponse(false);
    }
  };

  const getPlatformColor = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'google':
        return '#4285F4';
      case 'facebook':
        return '#1877F2';
      case 'yelp':
        return '#FF1A1A';
      default:
        return '#757575';
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Customer Reviews
      </Typography>

      {error && !isResponseDialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        {reviews.map((review) => (
          <Card key={review.id}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box display="flex" alignItems="center">
                  <Chip
                    label={review.platform}
                    size="small"
                    style={{ backgroundColor: getPlatformColor(review.platform), color: 'white' }}
                  />
                  <Box ml={1}>
                    <Rating value={review.rating} readOnly size="small" />
                  </Box>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {new Date(review.created_at).toLocaleDateString()}
                </Typography>
              </Box>

              <Typography variant="body1" gutterBottom>
                {review.content}
              </Typography>

              <Typography variant="subtitle2" color="textSecondary">
                - {review.author}
              </Typography>

              {review.response && (
                <Box mt={2} ml={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Your Response:</strong>
                  </Typography>
                  <Typography variant="body2">{review.response}</Typography>
                </Box>
              )}

              {!review.response && (
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleOpenResponseDialog(review)}
                  >
                    Respond
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}

        {reviews.length === 0 && !isLoading && !error && (
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary">
              No reviews to display.
            </Typography>
          </Box>
        )}
      </Stack>

      <Dialog
        open={isResponseDialogOpen}
        onClose={handleCloseResponseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Respond to Review
          <IconButton
            aria-label="close"
            onClick={handleCloseResponseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box mb={2}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Original Review:
            </Typography>
            <Typography variant="body1">{selectedReview?.content}</Typography>
          </Box>
          <TextField
            label="Your Response"
            multiline
            rows={4}
            fullWidth
            value={response}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResponse(e.target.value)}
            disabled={isGeneratingResponse || isSubmittingResponse}
          />
          <Box mt={2}>
            <Button
              variant="outlined"
              onClick={handleGenerateResponse}
              startIcon={isGeneratingResponse ? <CircularProgress size={20} /> : <StarIcon />}
              disabled={isGeneratingResponse || isSubmittingResponse}
            >
              {isGeneratingResponse ? 'Generating...' : 'Generate AI Response'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseResponseDialog}
            disabled={isSubmittingResponse}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitResponse}
            disabled={!response || isSubmittingResponse}
            startIcon={isSubmittingResponse && <CircularProgress size={20} />}
          >
            {isSubmittingResponse ? 'Submitting...' : 'Submit Response'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reviews;
